import { useState, useEffect, useCallback } from 'react';

interface NavigationPreferences {
  sidebarCollapsed: boolean;
  pinnedItems: string[];
  recentlyVisited: string[];
  viewDensity: 'comfortable' | 'compact' | 'spacious';
}

const DEFAULT_PREFERENCES: NavigationPreferences = {
  sidebarCollapsed: false,
  pinnedItems: [],
  recentlyVisited: [],
  viewDensity: 'comfortable',
};

const STORAGE_KEY = 'nav_preferences';
const MAX_RECENT_ITEMS = 5;

export const useNavigationPreferences = () => {
  const [preferences, setPreferences] = useState<NavigationPreferences>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) } : DEFAULT_PREFERENCES;
    } catch {
      return DEFAULT_PREFERENCES;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  }, [preferences]);

  const toggleSidebar = useCallback(() => {
    setPreferences(prev => ({
      ...prev,
      sidebarCollapsed: !prev.sidebarCollapsed,
    }));
  }, []);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setPreferences(prev => ({
      ...prev,
      sidebarCollapsed: collapsed,
    }));
  }, []);

  const pinItem = useCallback((path: string) => {
    setPreferences(prev => ({
      ...prev,
      pinnedItems: prev.pinnedItems.includes(path)
        ? prev.pinnedItems.filter(p => p !== path)
        : [...prev.pinnedItems, path],
    }));
  }, []);

  const addRecentlyVisited = useCallback((path: string) => {
    setPreferences(prev => {
      const filtered = prev.recentlyVisited.filter(p => p !== path);
      return {
        ...prev,
        recentlyVisited: [path, ...filtered].slice(0, MAX_RECENT_ITEMS),
      };
    });
  }, []);

  const setViewDensity = useCallback((density: 'comfortable' | 'compact' | 'spacious') => {
    setPreferences(prev => ({
      ...prev,
      viewDensity: density,
    }));
  }, []);

  const resetPreferences = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    preferences,
    toggleSidebar,
    setSidebarCollapsed,
    pinItem,
    addRecentlyVisited,
    setViewDensity,
    resetPreferences,
  };
};
